// How much text a story page carries, chosen by the parent in the wizard (owner decision 2026-10-16).
// 'picture' is the behaviour every book had before this existed; 'story' is for ages 4-7.
export type ReadingLevel = 'picture' | 'story';
export const DEFAULT_READING_LEVEL: ReadingLevel = 'picture';

export const READING_LEVELS: Record<ReadingLevel, {
  label: string; ages: string; minSentences: number; maxSentences: number; minWords: number; maxWords: number; blurb: string;
}> = {
  picture: { label: 'Picture book', ages: 'ages 2–4', minSentences: 2, maxSentences: 3, minWords: 15, maxWords: 40,
    blurb: 'A few short sentences per page, read aloud in a minute.' },
  story: { label: 'Story time', ages: 'ages 4–7', minSentences: 4, maxSentences: 6, minWords: 60, maxWords: 90,
    blurb: 'A fuller story on each page, with more dialogue and detail.' },
};

// Character limits for scripts written without spaces. Per language, because density differs:
// Japanese kana runs ~2.7 characters per English word, so ja gets 120/250 (~40/90 words) so the
// picture-book default is unchanged and Story time stays above its 60-word minimum. zh keeps the
// spec's 70/160; th (not a supported language) keeps the same as zh.
const CHAR_LIMITS: Record<string, Record<ReadingLevel, number>> = {
  zh: { picture: 70, story: 160 },
  ja: { picture: 120, story: 250 },
  th: { picture: 70, story: 160 },
};

function charLang(language?: string | null): string | null {
  const l = (language || '').slice(0, 2).toLowerCase();
  return l in CHAR_LIMITS ? l : null;
}

export function parseReadingLevel(v: unknown): ReadingLevel {
  return v === 'story' || v === 'picture' ? v : DEFAULT_READING_LEVEL;
}

export function pageTextLimit(level: ReadingLevel, language?: string | null): { unit: 'words' | 'chars'; max: number } {
  const cl = charLang(language);
  return cl ? { unit: 'chars', max: CHAR_LIMITS[cl][level] } : { unit: 'words', max: READING_LEVELS[level].maxWords };
}

export function pageTextLength(text: string, language?: string | null): number {
  const t = (text || '').trim();
  if (!t) return 0;
  return charLang(language) ? t.replace(/\s+/g, '').length : t.split(/\s+/).length;
}

// For the preview editor's live counter: where a page stands against its reading-level limit.
export function pageTextStatus(text: string, level: ReadingLevel, language?: string | null): {
  length: number; max: number; unit: 'words' | 'chars'; over: boolean;
} {
  const { unit, max } = pageTextLimit(level, language);
  const length = pageTextLength(text, language);
  return { length, max, unit, over: length > max };
}

export function storyLengthPromptLines(level: ReadingLevel): { system: string; requirement: string; example: string } {
  const l = READING_LEVELS[level];
  const range = `${l.minSentences}-${l.maxSentences}`;
  const words = level === 'story' ? ` (${l.minWords}-${l.maxWords} words)` : '';
  return {
    system: `Write engaging story text with ${range} sentences per page${words}.`,
    requirement: `- Each page MUST have ${range} complete sentences${words}${level === 'story' ? ', for children aged 4-7: include dialogue and a small detail of what the characters see, hear or feel' : ''}`,
    example: `${range} sentences${words} focusing on action and emotion, NO outfit descriptions`,
  };
}
